import { useMemo, useState } from 'react';
import { Plus, Trash2, Key, Copy, Check, RefreshCw, Shuffle } from 'lucide-react';
import {
  Badge,
  Button,
  Explainer,
  IconButton,
  Input,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Toolbar } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  AES_GATEWAY_HOST,
  useAesScramblerStreams,
  useAddScramblerStream,
  useRemoveScramblerStream,
  useIssueScramblerKey,
  type AesScramblerKeyResponse,
} from '@/api/aes';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './Aes.module.css';

// ---- Copy button ------------------------------------------------------------

export function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <IconButton
      variant="ghost"
      size="sm"
      aria-label={copied ? 'Copied' : `Copy ${label}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {copied ? <Check size={14} color="var(--vn-success-solid)" /> : <Copy size={14} />}
    </IconButton>
  );
}

// ---- Issued key dialog ------------------------------------------------------

/** Action result — shown as a Dialog (DS overlay pattern), not an inline panel. */
function IssuedKeyDialog({
  result,
  onClose,
}: {
  result: AesScramblerKeyResponse;
  onClose: () => void;
}) {
  const row = (label: string, value: string) => (
    <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{label}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 0 }}>
        <span className={styles.eventName} style={{ wordBreak: 'break-all' }}>{value}</span>
        <CopyButton value={value} label={`${label} key`} />
      </span>
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Scrambler key — {result.r}</DialogTitle>
          <DialogDescription>
            Paste the HEX value into the CDN's "Third Party Key" field.
          </DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Alert tone="info" title="HEX or Base64?">
            Both encode the same AES-128 key — the required format depends on the
            encoder/CDN system. Most "Third Party Key" forms expect HEX; use
            Base64 only when the encoder explicitly asks for it.
          </Alert>
          {row('HEX', result.hex)}
          {row('Base64', result.base64)}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Playback auth guide ----------------------------------------------------

/** Swift-style UTC timestamp: YYYYMMDDhhmmss. */
function utcStamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}`;
}

/** Copyable code snippet, mono block + copy button (same pattern as key dialog). */
function Snippet({ code, label }: { code: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--vn-space-2)' }}>
      <pre className={styles.codeBlock} style={{ flex: 1, minWidth: 0 }}>{code}</pre>
      <CopyButton value={code} label={label} />
    </div>
  );
}

/**
 * Collapsible walkthrough of the authenticated playback flow: mint a bearer at
 * /drm/signed, download the key from /drm/keyfile with that bearer. For streams
 * whitelisted on this page the gateway serves its own scrambler key (no
 * upstream call) — the bytes match the HEX issued via "Issue key".
 */
function BearerFlowGuide({ exampleStream }: { exampleStream: string }) {
  // Window computed once per mount so copy-pasted examples stay valid ~1 hour.
  const { stime, etime } = useMemo(() => {
    const now = new Date();
    return { stime: utcStamp(now), etime: utcStamp(new Date(now.getTime() + 3600_000)) };
  }, []);

  const mintCmd = [
    `curl -s ${AES_GATEWAY_HOST}/drm/signed \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -d '{"kind":"live","r":"${exampleStream}","stime":"${stime}","etime":"${etime}"}'`,
  ].join('\n');
  const fetchCmd = [
    `curl -s ${AES_GATEWAY_HOST}/drm/keyfile \\`,
    `  -H "Authorization: Bearer <token>" -o key.bin`,
    ``,
    `xxd -p key.bin   # 16 raw bytes — hex matches the issued scrambler key`,
  ].join('\n');

  return (
    <Explainer
      title="How playback authentication works"
      description={`Mint a bearer at /drm/signed, then fetch the key from /drm/keyfile — ${AES_GATEWAY_HOST.replace('https://', '')}`}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <div className={styles.flowLine}>
          backend ──POST /drm/signed──▶ token ──player: GET /drm/keyfile + Bearer──▶ 16-byte AES key ──▶ decrypt segments
        </div>

        <div className={styles.guideStep}>
          <span className={styles.guideStepTitle}>1 · Backend mints a bearer token</span>
          <p className={styles.guideStepText}>
            Your backend (not the player) calls <code>/drm/signed</code> with the stream ID and a
            UTC validity window (<code>YYYYMMDDhhmmss</code>). The response is{' '}
            <code>{'{"token","expires_at","ttl"}'}</code> — hand <code>token</code> to the player.
            For streams whitelisted here, the gateway signs locally without contacting the
            upstream key server.
          </p>
          <Snippet code={mintCmd} label="mint command" />
        </div>

        <div className={styles.guideStep}>
          <span className={styles.guideStepTitle}>2 · Player downloads the key with the bearer</span>
          <p className={styles.guideStepText}>
            The player requests <code>/drm/keyfile</code> with{' '}
            <code>Authorization: Bearer &lt;token&gt;</code> (e.g. via an HLS request hook).
            The gateway verifies the signature and validity window, then returns the raw
            16-byte AES-128 key — identical bytes to the HEX issued on this page and pasted
            into the CDN form, so CDN-encrypted segments decrypt correctly.
          </p>
          <Snippet code={fetchCmd} label="key fetch command" />
        </div>

        <ul
          style={{
            display: 'grid',
            gap: 'var(--vn-space-2)',
            margin: 0,
            paddingLeft: 'var(--vn-space-4)',
            fontSize: 'var(--vn-font-size-sm)',
            color: 'var(--vn-fg-muted)',
            lineHeight: 'var(--vn-line-height-normal)',
          }}
        >
          <li>
            Each mint registers a DRM session (visible under <strong>Sessions</strong>) — revoking
            it makes the next key fetch return 403 immediately.
          </li>
          <li>
            The bearer expires at <code>etime</code>; expired or tampered tokens are rejected
            with 403.
          </li>
          <li>
            The CDN-side "Key URL In Playlist" (<code>/drm/keyfile?r=&lt;stream&gt;&amp;t=DTV&amp;p=0</code>)
            is the unauthenticated download path baked into <code>EXT-X-KEY</code> — both paths
            return the same key bytes.
          </li>
          <li>
            Streams <em>not</em> whitelisted here follow the same two steps, but the gateway
            proxies the upstream key server instead of serving its own key.
          </li>
        </ul>
      </div>
    </Explainer>
  );
}

// ---- Main component ---------------------------------------------------------

/**
 * Scrambler stream whitelist manager. Ops can add/remove whitelisted stream IDs
 * and issue the per-stream AES-128 key (hex + base64) to paste into the CDN
 * "Third Party Key Server" form.
 */
export function AesScrambler({ serviceId }: { serviceId?: string }) {
  const sid = serviceId ?? '';
  const { data, isLoading, isError, error, refetch, isFetching } = useAesScramblerStreams(serviceId);
  const add = useAddScramblerStream(sid);
  const remove = useRemoveScramblerStream(sid);
  const issue = useIssueScramblerKey(sid);
  const { show } = useToast();

  const [addOpen, setAddOpen] = useState(false);
  const [newStream, setNewStream] = useState('');
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);
  const [issuedFor, setIssuedFor] = useState<string | null>(null);

  // Detect 503 (Redis unavailable — management disabled, serve still works)
  const is503 =
    isError && (error as { response?: { status?: number } })?.response?.status === 503;

  const streams = useMemo(() => data?.streams ?? [], [data]);

  const columns = useMemo<ColumnDef<string, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Stream',
        cell: ({ row }) => (
          <span className={styles.eventName}>{row.original}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 180,
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const issuing = issue.isPending && issuedFor === r;
          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
              <Button
                size="sm"
                variant="secondary"
                startIcon={<Key size={14} />}
                disabled={issuing || remove.isPending}
                onClick={() => {
                  setIssuedFor(r);
                  issue.mutate(r, {
                    onSuccess: () => trackFeatureAction('aes', 'key', 'create'),
                    onError: (err) => {
                      const status = (err as { response?: { status?: number } })?.response?.status;
                      show({
                        tone: 'danger',
                        title: status === 404 ? `Stream "${r}" not found` : "Couldn't issue key",
                      });
                      setIssuedFor(null);
                    },
                  });
                }}
              >
                Issue key
              </Button>
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={`Remove ${r}`}
                disabled={remove.isPending || add.isPending}
                onClick={() => setPendingRemove(r)}
              >
                <Trash2 size={14} />
              </IconButton>
            </div>
          );
        },
      },
    ],
    [issue, issuedFor, remove.isPending, add.isPending, show],
  );

  // Show issued key result in a dialog/panel when it comes back
  const issuedResult =
    issue.isSuccess && issue.data && issuedFor === issue.variables ? issue.data : null;

  const handleAdd = () => {
    const r = newStream.trim();
    if (!r) return;
    add.mutate(r, {
      onSuccess: () => {
        setNewStream('');
        setAddOpen(false);
        show({ tone: 'success', title: `Stream "${r}" added` });
        trackFeatureAction('aes', 'key', 'enable');
      },
      onError: () => show({ tone: 'danger', title: "Couldn't add stream" }),
    });
  };

  if (isLoading && !data) {
    return <Skeleton variant="rect" height={140} />;
  }

  return (
    <>
      {is503 && (
        <Alert tone="warning" title="Stream management requires Redis on the gateway">
          Redis is not connected — the whitelist is static from env. Add/remove is temporarily unavailable.
        </Alert>
      )}

      {isError && !is503 && (
        <Alert tone="danger" title="Couldn't load streams">
          {(error as { message?: string })?.message ?? 'Unknown error'}
        </Alert>
      )}

      {/* One control strip above the table: count on the left, add + refresh on
          the right (Toolbar = the console's standard filter/action row). */}
      <Toolbar
        start={
          data && (
            <Badge tone="neutral" appearance="subtle" size="sm">
              {data.count} stream{data.count === 1 ? '' : 's'}
              {data.redis_backed ? '' : ' · static'}
            </Badge>
          )
        }
        end={
          // Toolbar controls ride at the default (md) height — `sm` is for
          // Section/PageHeader actions, and mixing the two reads misaligned.
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <Button
              variant="secondary"
              startIcon={<RefreshCw size={15} />}
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              Refresh
            </Button>
            <Button
              startIcon={<Plus size={15} />}
              disabled={is503}
              onClick={() => setAddOpen(true)}
            >
              Add stream
            </Button>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={streams}
        getRowId={(r) => r}
        dense
        emptyState={
          <EmptyState
            icon={<Shuffle size={20} />}
            title="No whitelisted streams"
            description="Add a stream ID to allow scrambler key issuance."
            actions={
              <Button size="sm" startIcon={<Plus size={14} />} disabled={is503} onClick={() => setAddOpen(true)}>
                Add stream
              </Button>
            }
          />
        }
      />

      {/* Walkthrough of the authenticated /drm/signed → /drm/keyfile flow,
          using a real whitelisted stream in the curl examples when available. */}
      {/* space-4 = standard intra-Section block gap (space-6 is Section-level). */}
      <div style={{ marginTop: 'var(--vn-space-4)' }}>
        <BearerFlowGuide exampleStream={streams[0] ?? 'digitalwc1'} />
      </div>

      {/* Add stream dialog (house pattern: toolbar action button → Dialog form). */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setNewStream(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add stream</DialogTitle>
            <DialogDescription>
              Whitelist a stream ID so the gateway can issue scrambler keys for it.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <Field label="Stream ID" required description="Takes effect within ~5 seconds.">
              {(p) => (
                <Input
                  {...p}
                  autoFocus
                  placeholder="e.g. digitalwc2"
                  value={newStream}
                  onChange={(e) => setNewStream(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  disabled={add.isPending}
                />
              )}
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button
              startIcon={<Plus size={14} />}
              disabled={!newStream.trim() || add.isPending}
              onClick={handleAdd}
            >
              {add.isPending ? 'Adding…' : 'Add stream'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issued key result — modal, dismiss resets the mutation so re-issuing works. */}
      {issuedResult && (
        <IssuedKeyDialog
          result={issuedResult}
          onClose={() => {
            issue.reset();
            setIssuedFor(null);
          }}
        />
      )}

      {/* Remove confirm dialog */}
      <ConfirmDialog
        open={pendingRemove != null}
        onOpenChange={(o) => !o && setPendingRemove(null)}
        title={`Remove stream "${pendingRemove}"?`}
        description={`Stream "${pendingRemove}" will no longer be allowed to request keys from the gateway. Takes effect within ~5 seconds.`}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (!pendingRemove) return;
          const r = pendingRemove;
          remove.mutate(r, {
            onSuccess: () => {
              show({ tone: 'success', title: `Stream "${r}" removed` });
              trackFeatureAction('aes', 'key', 'disable');
              setPendingRemove(null);
            },
            onError: () => {
              show({ tone: 'danger', title: "Couldn't remove stream" });
              setPendingRemove(null);
            },
          });
        }}
      />
    </>
  );
}

export default AesScrambler;
